import {
  Box,
  Container,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { EmptyStateIcon, Filter } from "assets/Icon";
import {
  getInvestmentPortfolioHistoryForm,
  getInvestmentPortfolioHistoryList,
} from "store/slices/investment/investmentSlice";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import EmptyState from "components/EmptyState";
import { INVESTMENT_HISTORY_HEAD } from "./constants";
import InvestmentDetailModal from "./components/InvestmentDetailModal";
import InvestmentPortfolioFilters from "./InvestmentPortfolioFilters";
import InvestmentPortfolioHistoryBox from "./components/HistoryBox";
import NumberFormats from "components/NumberFormats";
import Pagination from "components/Pagination";
import { ROUTE_PATHS } from "routes/paths";
import Table from "components/Table";
import TableSkeleton from "components/Skeletons/Table.skeleton";
import WrapperAmount from "pages/Wallet/Component/TableSpotHistory/Component/WrapperAmount";
import styled from "@emotion/styled";
import useDispatch from "hooks/useDispatch";
import useIsMobileView from "hooks/useIsMobileView";
import { useSelector } from "react-redux";

function InvestmentPortfolio() {
  const isMobileView = useIsMobileView();
  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const investmentHistoryList = useSelector(
    (state) => state.investment.investmentHistoryList
  );

  const [searchParams] = useSearchParams();

  const page = +searchParams.get("page") || 1;
  const perPage = +searchParams.get("perPage") || 10;
  const portfolioId = searchParams.get("portfolioId") || "";
  const endDateInvestmentEnd = searchParams.get("endDateInvestmentEnd") || "";
  const endDateInvestmentStart =
    searchParams.get("endDateInvestmentStart") || "";
  const startDateInvestmentEnd =
    searchParams.get("startDateInvestmentEnd") || "";
  const startDateInvestmentStart =
    searchParams.get("startDateInvestmentStart") || "";

  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading] = useState(false);

  const [openInvestmentDetails, setOpenInvestmentDetails] = useState(false);
  const [investmentDetails, setInvestmentDetails] = useState(null);

  const handleChangeTab = (_, newValue) => {
    navigate(newValue);
  };

  const handleGetInvestmentPortfolioHistoryList = async ({ page, perPage }) => {
    setLoading(true);
    await dispatch(
      getInvestmentPortfolioHistoryList({
        portfolioId,
        endDateInvestmentEnd,
        endDateInvestmentStart,
        startDateInvestmentEnd,
        startDateInvestmentStart,
        pageSize: perPage,
        pageNumber: page,
      })
    );
    setLoading(false);
  };

  const handleChangePaginate = ({ page, perPage }) => {
    handleGetInvestmentPortfolioHistoryList({ page, perPage });
  };

  const profitColor = useCallback((item) => {
    if (+item?.percentageProfitOrLoss > 0) {
      return theme.palette.success.main
    }

    if (+item?.percentageProfitOrLoss < 0) {
      return theme.palette.error.main
    }

    return theme.palette.text.main

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    handleGetInvestmentPortfolioHistoryList({ page, perPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    portfolioId,
    endDateInvestmentEnd,
    endDateInvestmentStart,
    startDateInvestmentEnd,
    startDateInvestmentStart,
  ]);

  useEffect(() => {
    dispatch(getInvestmentPortfolioHistoryForm());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedItems = investmentHistoryList?.data?.map((item) => ({
    ...item,
    coin: <WrapperAmount data={item} />,
    portfolio: item?.portfolio,

    deposit: (
      <Box sx={{ display: "flex", gap: 4, justifyContent: "center" }}>
        <Typography
          color="text.main"
          fontWeight={500}
          variant="h6"
          component="span"
        >
          <NumberFormats value={item?.amountDeposit} />{" "}
        </Typography>

        <Typography
          color="text.main"
          fontWeight={500}
          variant="h6"
          component="span"
        >
          <NumberFormats value={item?.feeDeposit} />{" "}
        </Typography>
      </Box>
    ),

    investment: (
      <Box sx={{ display: "flex", gap: 4, justifyContent: "center" }}>
        <Typography sx={{ flex: 1, textAlign: 'right' }} color="text.secondary" fontWeight={500} variant="subtitle1">
          <NumberFormats value={item?.total} />
        </Typography>

        <Typography
          color="text.main"
          fontWeight={500}
          variant="subtitle1"
          component="span"
          sx={{ display: "inline-flex", flexDirection: "column", flex: 1 }}
        >
          <Typography
            color="text.main"
            fontWeight={500}
            variant="subtitle1"
            component="span"
          >
            <NumberFormats value={item?.profitOrLoss} />
          </Typography>
          <Typography
            color={profitColor(item)}
            fontWeight={500}
            variant="subtitle1"
            component="span"
          >
            <NumberFormats value={item?.percentageProfitOrLoss} /> %
          </Typography>
        </Typography>

        <Typography
          sx={{ flex: 1, textAlign: 'left' }}
          color="text.secondary"
          fontWeight={500}
          variant="subtitle1"
          component="span"
        >
          <NumberFormats value={item?.feeWithdraw} />
        </Typography>
      </Box>
    ),
    withdraw: (
      <Typography
        color="text.main"
        fontWeight={500}
        variant="h6"
        component="span"
      >
        <NumberFormats value={item?.netAmountWithdraw} />
      </Typography>
    ),
    action: (
      <IconButtonStyle
        aria-label="history"
        size="small"
        onClick={() => {
          setInvestmentDetails(item);
          setOpenInvestmentDetails(true);
        }}
      >
        <ion-icon name="eye-outline" />
      </IconButtonStyle>
    ),
  }));

  return (
    <>
      <Container maxWidth="100%">
        <Grid container spacing={2} alignItems="center">
          {isMobileView && (
            <Grid item xs={12}>
              <TabsStyle value={location.pathname} onChange={handleChangeTab}>
                <Tab
                  label=" Request History"
                  value={ROUTE_PATHS.history.request}
                />
                <Tab
                  label="Investment History"
                  value={ROUTE_PATHS.history.investment}
                />
              </TabsStyle>
            </Grid>
          )}

          <Grid item xs={6}>
            <Grid container>
              <Typography color="text.main" variant="h4">
                Investment History
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={6} textAlign="right">
            <IconButton
              onClick={() => {
                setShowFilter((v) => !v);
              }}
              aria-label="filter"
            >
              <Filter color={theme.palette.text.main} />
            </IconButton>
          </Grid>
        </Grid>

        {showFilter && <InvestmentPortfolioFilters />}

        <Grid container mt={2} spacing={2}>
          <>
            {isMobileView ? (
              <>
                {investmentHistoryList?.data?.length ? (
                  <>
                    {investmentHistoryList?.data?.map((investment) => (
                      <Grid item xs={12} sm={6}>
                        <InvestmentPortfolioHistoryBox
                          item={investment}
                          key={investment?.id}
                        />
                      </Grid>
                    ))}
                  </>
                ) : (
                  <Grid item xs={12}>
                    <EmptyState
                      text="No History"
                      icon={
                        <EmptyStateIcon color={theme.palette.text.secondary} />
                      }
                    />
                  </Grid>
                )}
              </>
            ) : (
              <>
                {!loading ? (
                  <Grid item xs={12}>
                    <Table
                      headers={INVESTMENT_HISTORY_HEAD}
                      items={formattedItems}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <TableSkeleton />
                  </Grid>
                )}
              </>
            )}
          </>

          <Grid item xs={12}>
            <Pagination
              perPage={investmentHistoryList?.pageSize}
              count={investmentHistoryList?.totalPages}
              onChange={handleChangePaginate}
            />
          </Grid>
        </Grid>
      </Container>

      <InvestmentDetailModal
        open={openInvestmentDetails}
        investmentDetails={investmentDetails}
        onClose={() => {
          setInvestmentDetails(null);
          setOpenInvestmentDetails(false);
        }}
      />
    </>
  );
}

const IconButtonStyle = styled(IconButton)(({ theme }) => ({
  "ion-icon": {
    color: theme.palette.text.secondary,
    fontSize: "22px",
  },
}));

const TabsStyle = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.modules.border}`,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(3),
  width: "100%",

  ".MuiTabs-flexContainer": {
    justifyContent: "space-around",
  },
}));

export default InvestmentPortfolio;
